import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid2 as Grid,
  Button,
  IconButton,
  Stack,
  Autocomplete,
  CircularProgress,
  Paper,
  alpha,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline,
  SaveOutlined,
  CloseOutlined,
  InfoOutlined,
  ScienceOutlined,
} from '@mui/icons-material';
import { message } from 'antd';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { addParameters, getStandardParameters } from '../../../../helpers/api';

const AddParameterForm = ({ closeModal, reload, measurementId }) => {
  const [parameterCollection, setParameterCollection] = useState([]);
  const [loading, setLoading] = useState(false);
  const [parameterData, setParameterData] = useState([]);
  const [parameterList, setParameterList] = useState([]);

  console.log('parameterList', parameterList);

  // Fetch standard parameters on mount
  useEffect(() => {
    fetchParameters();
  }, []);

  const fetchParameters = () => {
    getStandardParameters()
      .then((response) => {
        const parameters = response?.result?.data || [];
        const modified = parameters.map((item) => ({
          ...item,
          label: `${item.unit} - (${item.siUnit})`,
        }));
        setParameterData(parameters);
        setParameterList(modified);
      })
      .catch(() => {
        message.error('Failed to fetch parameters');
      });
  };

  // Formik for the "local" add part
  const formik = useFormik({
    initialValues: {
      parameterObj: null,
      nesrea: '',
      world: '',
    },
    validationSchema: Yup.object({
      parameterObj: Yup.object()
        .nullable()
        .required('Please select a parameter'),
      nesrea: Yup.string().required('NESREA limit is required'),
    }),
    onSubmit: (values, { resetForm }) => {
      const exists = parameterCollection?.find(
        (x) => x.parameterId === values.parameterObj.parameterId
      );

      if (!exists) {
        const newItem = {
          parameterId: values.parameterObj.parameterId,
          unit: values.parameterObj.unit,
          siUnit: values.parameterObj.siUnit,
          nesrea: values.nesrea,
          world: values.world || '-',
        };
        setParameterCollection([...parameterCollection, newItem]);
        resetForm();
      } else {
        message.error(`This parameter is already in your collection`);
      }
    },
  });

  const handleRemoveFromCollection = (parameterId) => {
    setParameterCollection((prev) => prev.filter((x) => x.parameterId !== parameterId));
  };

  const handleSubmitFinal = async () => {
    if (parameterCollection.length === 0) return;

    setLoading(true);
    try {
      await addParameters(parameterCollection, measurementId);
      message.success(`Parameters added successfully`);
      reload();
      closeModal();
    } catch (error) {
      message.error(error?.message || 'Submission failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* LOCAL ADD FORM SECTION */}
      <Box component="form" onSubmit={formik.handleSubmit} sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid size={12}>
            <Autocomplete
              id="parameter-select"
              disablePortal
              options={parameterList}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) => option.parameterId === value?.parameterId}
              value={formik.values.parameterObj}
              onChange={(_, newValue) => formik.setFieldValue('parameterObj', newValue)}
              // This ensures the dropdown is visible over modals
              slotProps={{
                popper: { sx: { zIndex: 1300 } },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Parameter Name"
                  size="small"
                  error={formik.touched.parameterObj && Boolean(formik.errors.parameterObj)}
                  helperText={formik.touched.parameterObj && formik.errors.parameterObj}
                  // THE FIX: Target the root of the outlined input specifically
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                    },
                  }}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              name="nesrea"
              label="NESREA Limit"
              size="small"
              value={formik.values.nesrea}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.nesrea && Boolean(formik.errors.nesrea)}
              helperText={formik.touched.nesrea && formik.errors.nesrea}
              slotProps={{ input: { sx: { borderRadius: '10px' } } }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              name="world"
              label="World Bank Limit"
              size="small"
              value={formik.values.world}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              slotProps={{ input: { sx: { borderRadius: '10px' } } }}
            />
          </Grid>

          <Grid size={12}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                bgcolor: alpha('#1976d2', 0.05),
                borderRadius: '10px',
                border: '1px dashed',
                borderColor: alpha('#1976d2', 0.3),
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <InfoOutlined sx={{ fontSize: 16, color: 'primary.main' }} />
                <Typography variant="caption" fontWeight={700} color="primary.main">
                  FORMATTING HINT
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block">
                Use <strong>{`<`}</strong> (Less Than), <strong>{`>`}</strong> (Greater Than), or{' '}
                <strong>{`-`}</strong> (Range).
              </Typography>
            </Paper>
          </Grid>

          <Grid size={12} sx={{ textAlign: 'right' }}>
            <Button
              type="submit"
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px' }}
            >
              Add to Collection
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* COLLECTION PREVIEW SECTION */}
      {parameterCollection.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            color="text.secondary"
            sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <ScienceOutlined fontSize="small" /> Parameter Collection Preview
          </Typography>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '12px' }}
          >
            <Table size="small">
              <TableHead sx={{ bgcolor: alpha('#F4F6F8', 0.6) }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>PARAMETER</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>SI UNIT</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>NESREA</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>WORLD BANK</TableCell>
                  <TableCell align="right"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {parameterCollection.map((row) => (
                  <TableRow
                    key={row.parameterId}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {row.unit}
                    </TableCell>
                    <TableCell>{row.siUnit}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.nesrea}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          height: 20,
                          bgcolor: alpha('#4caf50', 0.1),
                          color: '#2e7d32',
                        }}
                      />
                    </TableCell>
                    <TableCell>{row.world || '-'}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveFromCollection(row.parameterId)}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* FINAL FORM ACTIONS */}
      <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button
          onClick={closeModal}
          disabled={loading}
          sx={{ textTransform: 'none', fontWeight: 700, color: 'text.secondary' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmitFinal}
          variant="contained"
          disabled={!parameterCollection.length || loading}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SaveOutlined />}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 4 }}
        >
          {loading ? 'Saving...' : 'Save All Parameters'}
        </Button>
      </Stack>
    </Box>
  );
};

export default AddParameterForm;
